/* eslint-disable @next/next/no-img-element */

import { Content, ContentCard } from '@yleisradio/areena-types';
import { Container } from 'components/Container';
import { GridContainer, GridElement } from 'components/Grid';
import { useBrazeSdk } from 'contexts/BrazeSdkContext';
import { BrazeContentCard as BrazeContentCardType } from 'contexts/BrazeSdkContext/types';
import { isContentCardPlaceholder } from 'utils/content';
import { sanitizeHtmlId } from 'utils/ui-helpers';
import styles from './BrazeContentCard.module.scss';
import { Button } from 'components/Button';
import { useEffect, useRef } from 'react';

type Props = {
  card: BrazeContentCardType;
};

export const BrazeContentCard: React.FunctionComponent<Props> = ({ card }) => {
  const { braze } = useBrazeSdk();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (braze && rootRef.current) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          braze.logContentCardImpressions([card]);
          observer.disconnect();
        }
      });

      observer.observe(rootRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [braze, card]);

  if (!braze) return null;

  if (card instanceof braze.ControlCard) {
    return <div ref={rootRef} />;
  }

  if (
    !(card instanceof braze.ClassicCard) &&
    !(card instanceof braze.CaptionedImage)
  ) {
    return null;
  }

  const headingId = sanitizeHtmlId(`content-card-${card.id}-heading`);

  return (
    <div
      className={styles.root}
      role="group"
      aria-labelledby={headingId}
      ref={rootRef}
    >
      <Container>
        <GridContainer>
          <GridElement mobile={0} desktopSmall={1} />

          <GridElement
            mobile={12}
            mobileLandscape={6}
            orderMobileLandscape={2}
            desktopSmall={11}
          >
            <div className={styles.imageContainer}>
              {card.imageUrl && (
                <img
                  alt=""
                  className={styles.image}
                  loading="lazy"
                  src={card.imageUrl}
                />
              )}
            </div>
          </GridElement>

          <GridElement mobile={12} mobileLandscape={6} desktopSmall={10}>
            <div className={styles.textContent}>
              <h2 className={styles.title} id={headingId}>
                {card.title}
              </h2>

              <p className={styles.description}>{card.description}</p>

              {card.linkText && card.url ? (
                <div className={styles.buttonContainer}>
                  <Button
                    onClick={() => {
                      braze.logContentCardClick(card);
                    }}
                    pointer={{ uri: card.url }}
                    size="sm"
                    text={card.linkText}
                    variant="primary"
                  />
                </div>
              ) : null}
            </div>
          </GridElement>

          <GridElement mobile={0} desktopSmall={1} />
        </GridContainer>
      </Container>
    </div>
  );
};

export function getContentCardIndex(
  contentCard: ContentCard,
  contentArray: Content[]
): number {
  const indexInContentCards = contentArray
    .filter(isContentCardPlaceholder)
    .indexOf(contentCard);

  if (indexInContentCards === -1)
    throw new Error('Placeholder not found in content array');

  return indexInContentCards;
}
